import React, { useMemo, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import LeagueTable from "../LeagueTable/LeagueTable";
import MatchTable from "../Match/MatchTable";
import NewMatchButton from "../Match/NewMatchButton";

const knockoutOrder = [
  "LAST_32",
  "LAST_16",
  "QUARTER_FINAL",
  "SEMI_FINAL",
  "FINAL",
];

const formatLabel = (value, fallback) =>
  (value || fallback || "Unspecified").replace(/_/g, " ");

const TabPanel = ({ value, current, children }) => {
  if (value !== current) return null;
  return <Box mt={2}>{children}</Box>;
};

const SeasonTables = ({
  matches = [],
  matchesWithPermission = [],
  canEdit,
  seasonId,
  teams = [],
}) => {
  const [tab, setTab] = useState("league");
  const [teamFilter, setTeamFilter] = useState(null);

  const partitioned = useMemo(() => {
    const leagueMatches = [];
    const groupMatchesByGroup = new Map();
    const knockoutMatchesByStage = new Map();

    matches.forEach((match) => {
      if (match.matchType === "GROUP") {
        const key = match.groupType || "Unspecified";
        const existing = groupMatchesByGroup.get(key) || [];
        existing.push(match);
        groupMatchesByGroup.set(key, existing);
        return;
      }

      if (match.matchType === "KNOCKOUT") {
        const key = match.knockoutType || "UNSPECIFIED";
        const existing = knockoutMatchesByStage.get(key) || [];
        existing.push(match);
        knockoutMatchesByStage.set(key, existing);
        return;
      }

      leagueMatches.push(match);
    });

    const groupEntries = Array.from(groupMatchesByGroup.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );

    const knockoutEntries = Array.from(knockoutMatchesByStage.entries()).sort(
      (a, b) =>
        (knockoutOrder.indexOf(a[0]) === -1
          ? Number.POSITIVE_INFINITY
          : knockoutOrder.indexOf(a[0])) -
        (knockoutOrder.indexOf(b[0]) === -1
          ? Number.POSITIVE_INFINITY
          : knockoutOrder.indexOf(b[0]))
    );

    return { leagueMatches, groupEntries, knockoutEntries };
  }, [matches]);

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          aria-label="match type tabs"
        >
          <Tab value="league" label="League" />
          <Tab value="groups" label="Groups" />
          <Tab value="knockout" label="Knockout" />
        </Tabs>
        {canEdit && (
          <NewMatchButton seasonId={seasonId} teams={teams} />
        )}
      </Box>

      <TabPanel value="league" current={tab}>
        {partitioned.leagueMatches.length === 0 ? (
          <Typography color="text.secondary">No league matches yet.</Typography>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Standings
            </Typography>
            <LeagueTable matches={partitioned.leagueMatches} onTeamFilter={setTeamFilter} />
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                Fixtures
              </Typography>
              <MatchTable
                matches={partitioned.leagueMatches}
                matchesWithPermission={matchesWithPermission}
                canEdit={canEdit}
                seasonId={seasonId}
                teams={teams}
                teamFilter={teamFilter}
                onClearTeamFilter={() => setTeamFilter(null)}
              />
            </Box>
          </Box>
        )}
      </TabPanel>

      <TabPanel value="groups" current={tab}>
        {partitioned.groupEntries.length === 0 ? (
          <Typography color="text.secondary">No group matches yet.</Typography>
        ) : (
          <Box>
            {partitioned.groupEntries.map(([group, groupMatches]) => (
              <Box key={group} mb={3}>
                <Typography variant="h6" gutterBottom>{`Group ${formatLabel(
                  group,
                  ""
                )}`}</Typography>
                <LeagueTable matches={groupMatches} onTeamFilter={setTeamFilter} />
              </Box>
            ))}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                All Group Fixtures
              </Typography>
              <MatchTable
                matches={partitioned.groupEntries.reduce(
                  (acc, [_, matches]) => [...acc, ...matches],
                  []
                )}
                matchesWithPermission={matchesWithPermission}
                canEdit={canEdit}
                seasonId={seasonId}
                teams={teams}
                teamFilter={teamFilter}
                onClearTeamFilter={() => setTeamFilter(null)}
              />
            </Box>
          </Box>
        )}
      </TabPanel>

      <TabPanel value="knockout" current={tab}>
        {partitioned.knockoutEntries.length === 0 ? (
          <Typography color="text.secondary">
            No knockout matches yet.
          </Typography>
        ) : (
          <Box>
            {partitioned.knockoutEntries.map(([stage, stageMatches]) => (
              <Box key={stage} mb={3}>
                <Typography variant="h6" gutterBottom>{`Knockout: ${formatLabel(
                  stage,
                  ""
                )}`}</Typography>
                <LeagueTable matches={stageMatches} onTeamFilter={setTeamFilter} />
              </Box>
            ))}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                All Knockout Fixtures
              </Typography>
              <MatchTable
                matches={partitioned.knockoutEntries.reduce(
                  (acc, [_, matches]) => [...acc, ...matches],
                  []
                )}
                matchesWithPermission={matchesWithPermission}
                canEdit={canEdit}
                seasonId={seasonId}
                teams={teams}
                teamFilter={teamFilter}
                onClearTeamFilter={() => setTeamFilter(null)}
              />
            </Box>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default SeasonTables;
